import { useState } from 'react';
import { useStore } from '../../store/StoreContext.jsx';
import { genUid } from '../../store/uid.js';
import { getOwnerNameOptions } from '../../store/selectors.js';
import { estateDocCodes } from '../../store/estateDocs.js';
import { useMeetingContext } from './Peek.jsx';
import { PROFILES, PROFILE_ORDER } from '../../store/householdProfile.js';

// What opens underneath a figure.
//
// The client says "I actually retired last month" or "we don't spend that
// much", and the advisor changes it there and then. There is no second copy of
// any figure to keep in step: every input here writes to the questionnaire's
// own field, the same one the eMoney push reads, so a correction made in the
// room is a correction everywhere (Eugene and John, 2026-09-29).
//
// These are the contents of a Peek card, never laid out on the page itself: a
// figure that is a sum opens to the rows it is made of, and those rows are
// what gets edited. The page recalculates as they change, because the
// questionnaire always has; whether advisors would rather note a change and
// revisit it is a question still open with them. A drawing made over the old
// figure is kept, not moved, which is the safeguard that matters.

function getAt(obj, path) {
  return String(path).split('.').reduce((o, k) => (o === null || o === undefined ? undefined : o[k]), obj);
}

const hasContent = r => r && Object.keys(r).some(k => !k.startsWith('_') && String(r[k] ?? '').trim() !== '');

/**
 * The store, wrapped so a row edit rewrites the whole array the way the
 * questionnaire does.
 *
 * A blank row is an unused line of the questionnaire and is not listed. But a
 * row the advisor has just added is blank too, and used to vanish the moment it
 * was added, leaving nothing to type into (QA review, 2026-09-30). So
 * the rows added while this card is open are remembered by their id and listed
 * however empty they are, and each can be taken away again with nothing
 * written in its place. A blank row reads as nothing downstream: the
 * assessment ignores a row with nothing in it and counts one with a name but
 * no value as a figure still missing, never as zero.
 */
export function useMeetingEdits() {
  const { data, update, readOnly } = useStore();
  const [drafts, setDrafts] = useState(() => new Set());
  const rowsAt = path => {
    const v = getAt(data, path);
    return Array.isArray(v) ? v : [];
  };
  return {
    data,
    readOnly,
    value: path => getAt(data, path),
    rows: rowsAt,
    /** The rows worth listing, each with its index in the stored array. */
    shown: path => rowsAt(path).map((r, i) => [r, i]).filter(([r]) => hasContent(r) || drafts.has(r?._uid)),
    isDraft: r => Boolean(r?._uid) && drafts.has(r._uid),
    setField: (path, value) => { if (!readOnly) update(path, value); },
    setRow: (path, index, patch) => {
      if (readOnly) return;
      update(path, rowsAt(path).map((r, i) => (i === index ? { ...r, ...patch } : r)));
    },
    addRow: (path, seed = {}) => {
      if (readOnly) return;
      const uid = genUid();
      setDrafts(prev => new Set(prev).add(uid));
      update(path, [...rowsAt(path), { _uid: uid, ...seed }]);
    },
    /** Take back a row added in this card. Rows that were already there are not removed from here. */
    dropDraft: (path, row) => {
      if (readOnly || !row?._uid || !drafts.has(row._uid)) return;
      update(path, rowsAt(path).filter(r => r?._uid !== row._uid));
      setDrafts(prev => {
        const next = new Set(prev);
        next.delete(row._uid);
        return next;
      });
    },
  };
}

export function MoneyInput({ value, onChange, disabled, label }) {
  return (
    <span className="conv-money">
      <span aria-hidden="true">$</span>
      <input
        type="text"
        inputMode="numeric"
        aria-label={label}
        value={value ?? ''}
        placeholder="Not recorded"
        disabled={disabled}
        onChange={e => onChange(e.target.value)}
      />
    </span>
  );
}

export function TextInput({ value, onChange, disabled, label, placeholder }) {
  return (
    <input
      type="text"
      className="conv-text"
      aria-label={label}
      value={value ?? ''}
      placeholder={placeholder || ''}
      disabled={disabled}
      onChange={e => onChange(e.target.value)}
    />
  );
}

export function AddButton({ children, onClick }) {
  return <button type="button" className="conv-link" onClick={onClick}>{`+ ${children}`}</button>;
}

// Whose account it is, from the same names the questionnaire offers. Planning
// cannot be sent an account without an owner, so an account added here with
// only a name and a value would hold up the send until someone found it in the
// questionnaire (Jimmy, 2026-10-01). A value typed in the questionnaire that is
// not one of the names is kept and shown as it is.
function OwnerSelect({ value, options, onChange, disabled, label }) {
  const current = String(value ?? '').trim();
  const list = current && !options.includes(current) ? [...options, current] : options;
  return (
    <select className="conv-owner" aria-label={label} value={current} disabled={disabled} onChange={e => onChange(e.target.value)}>
      <option value="">Owner</option>
      {list.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

export function DropButton({ label, onClick }) {
  return <button type="button" className="conv-row-drop" aria-label={label} title={label} onClick={onClick}>&times;</button>;
}

// Annual spending is living expenses plus every expense row with no dates. A
// dated row is a future or past cost and is listed as such, not left out
// silently.
export function SpendingEditor() {
  const e = useMeetingEdits();
  const rows = e.rows('taxesExpenses.expenses');
  return (
    <div className="conv-rows">
      <label className="conv-row">
        <span>Living expenses, a year</span>
        <MoneyInput
          value={e.value('taxesExpenses.livingExpenses')}
          disabled={e.readOnly}
          label="Annual living expenses"
          onChange={v => e.setField('taxesExpenses.livingExpenses', v)}
        />
      </label>
      {rows.map((r, i) => {
        const dated = String(r?.startDate || '').trim() || String(r?.endDate || '').trim();
        return (
          <div className={`conv-row${dated ? ' is-dated' : ''}`} key={r._uid || i}>
            <TextInput value={r.description} disabled={e.readOnly} label={`Expense ${i + 1}`} placeholder="Expense"
              onChange={v => e.setRow('taxesExpenses.expenses', i, { description: v })} />
            <MoneyInput value={r.amount} disabled={e.readOnly} label={`Expense ${i + 1} annual amount`}
              onChange={v => e.setRow('taxesExpenses.expenses', i, { amount: v })} />
            {dated ? <small>Dated, so not in this year's figure</small> : null}
          </div>
        );
      })}
      {!e.readOnly && <AddButton onClick={() => e.addRow('taxesExpenses.expenses', { description: '', amount: '' })}>Add an expense</AddButton>}
    </div>
  );
}

// The name column is the questionnaire's: Custodian for an account, and
// Description for property and business interests.
const NAME_LABEL = { custodian: 'Custodian', description: 'Description' };

export const ASSET_GROUPS = [
  ['taxDeferred', 'Tax-deferred', 'custodian'],
  ['roth', 'Roth', 'custodian'],
  ['hsa', 'HSA', 'custodian'],
  ['taxable', 'Taxable', 'custodian'],
  ['cashCd', 'Cash and CDs', 'custodian'],
  ['realEstate', 'Real estate', 'description'],
  ['plan529', '529 plans', 'custodian'],
  ['businessOther', 'Business and other', 'description'],
];

/**
 * Every asset row the questionnaire holds, by category; `only` narrows the
 * categories. Without `only`, a category with nothing in it gets no heading of
 * its own but is still offered at the foot, so the first account in it can be
 * added from here.
 */
export function AssetEditor({ only }) {
  const e = useMeetingEdits();
  const owners = getOwnerNameOptions(e.data);
  const groups = only ? ASSET_GROUPS.filter(g => only.includes(g[0])) : ASSET_GROUPS;
  const listed = groups.map(g => [...g, e.shown(`assets.${g[0]}`)]);
  const empty = only ? [] : listed.filter(g => !g[3].length);
  return (
    <div className="conv-rows">
      {listed.map(([key, label, nameKey, shown]) => {
        const path = `assets.${key}`;
        if (!shown.length && !only) return null;
        return (
          <div className="conv-rowgroup" key={key}>
            <div className="conv-rowgroup-head">{label}</div>
            {shown.map(([r, i]) => (
              <div className={`conv-row has-owner${e.isDraft(r) ? ' is-draft' : ''}`} key={r._uid || i}>
                <TextInput value={r[nameKey]} disabled={e.readOnly} label={`${label} ${i + 1}`} placeholder={NAME_LABEL[nameKey]}
                  onChange={v => e.setRow(path, i, { [nameKey]: v })} />
                <OwnerSelect value={r.ownership} options={owners} disabled={e.readOnly} label={`${label} ${i + 1} owner`}
                  onChange={v => e.setRow(path, i, { ownership: v })} />
                <MoneyInput value={r.marketValue} disabled={e.readOnly} label={`${label} ${i + 1} value`}
                  onChange={v => e.setRow(path, i, { marketValue: v })} />
                {e.isDraft(r) ? <DropButton label={`Remove this new ${label} account`} onClick={() => e.dropDraft(path, r)} /> : null}
              </div>
            ))}
            {!e.readOnly && <AddButton onClick={() => e.addRow(path, { [nameKey]: '', marketValue: '' })}>Add an account</AddButton>}
          </div>
        );
      })}
      {!e.readOnly && empty.length ? (
        <div className="conv-rowgroup conv-add-elsewhere">
          <div className="conv-rowgroup-head">Add an account to</div>
          <div className="conv-add-list">
            {empty.map(([key, label, nameKey]) => (
              <AddButton key={key} onClick={() => e.addRow(`assets.${key}`, { [nameKey]: '', marketValue: '' })}>{label}</AddButton>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function LiabilityEditor() {
  const e = useMeetingEdits();
  const path = 'liabilities.items';
  const rows = e.shown(path);
  return (
    <div className="conv-rows">
      {rows.map(([r, i]) => (
        <div className={`conv-row${e.isDraft(r) ? ' is-draft' : ''}`} key={r._uid || i}>
          <TextInput value={r.description} disabled={e.readOnly} label={`Debt ${i + 1}`} placeholder="Debt"
            onChange={v => e.setRow(path, i, { description: v })} />
          <MoneyInput value={r.amount} disabled={e.readOnly} label={`Debt ${i + 1} balance`}
            onChange={v => e.setRow(path, i, { amount: v })} />
          {e.isDraft(r) ? <DropButton label="Remove this new debt" onClick={() => e.dropDraft(path, r)} /> : null}
        </div>
      ))}
      {!rows.length ? <p className="conv-quiet">No debts recorded.</p> : null}
      {!e.readOnly && <AddButton onClick={() => e.addRow(path, { description: '', amount: '' })}>Add a debt</AddButton>}
    </div>
  );
}

/** Assets and debts together, for the net worth figure. */
export function BalanceSheetEditor() {
  return (
    <>
      <div className="conv-peek-section">What you own</div>
      <AssetEditor />
      <div className="conv-peek-section">What you owe</div>
      <LiabilityEditor />
    </>
  );
}

export const INCOME_GROUPS = [
  ['employment', 'Employment', 'Add a job'],
  ['socialSecurity', 'Social Security', 'Add a Social Security benefit'],
  ['pension', 'Pension', 'Add a pension'],
  ['other', 'Other income', 'Add other income'],
];

/** The income rows, optionally one kind only, each a label and an annual amount. */
export function IncomeEditor({ only }) {
  const e = useMeetingEdits();
  const groups = only ? INCOME_GROUPS.filter(g => only.includes(g[0])) : INCOME_GROUPS;
  return (
    <div className="conv-rows">
      {groups.map(([key, label, add]) => {
        const path = `income.${key}`;
        const shown = e.shown(path);
        return (
          <div className="conv-rowgroup" key={key}>
            <div className="conv-rowgroup-head">{label}</div>
            {shown.map(([r, i]) => (
              <div className={`conv-row${e.isDraft(r) ? ' is-draft' : ''}`} key={r._uid || i}>
                <TextInput value={r.description} disabled={e.readOnly} label={`${label} ${i + 1}`} placeholder={label}
                  onChange={v => e.setRow(path, i, { description: v })} />
                <MoneyInput value={r.annualAmount} disabled={e.readOnly} label={`${r.description || label} annual amount`}
                  onChange={v => e.setRow(path, i, { annualAmount: v })} />
                {e.isDraft(r) ? <DropButton label={`Remove this new ${label} row`} onClick={() => e.dropDraft(path, r)} /> : null}
              </div>
            ))}
            {!e.readOnly && <AddButton onClick={() => e.addRow(path, { description: label, annualAmount: '' })}>{add}</AddButton>}
          </div>
        );
      })}
    </div>
  );
}

const STATUS_OPTIONS = [
  ['Employed', 'Working'],
  ['Self Employed', 'Self-employed'],
  ['Retired', 'Retired'],
  ['Not Employed', 'Not working'],
];

/** Employment status and retirement timing, per person. */
export function PeopleEditor({ people }) {
  const e = useMeetingEdits();
  return (
    <div className="conv-rows">
      {people.map(p => {
        const emp = e.value(`employment.${p.who}`) || {};
        return (
          <div className="conv-rowgroup" key={p.who}>
            <div className="conv-rowgroup-head">{p.name}</div>
            <label className="conv-row">
              <span>Employment</span>
              <select value={emp.status || ''} disabled={e.readOnly} aria-label={`${p.name} employment status`}
                onChange={ev => e.setField(`employment.${p.who}.status`, ev.target.value)}>
                <option value="">Not recorded</option>
                {STATUS_OPTIONS.map(([v, w]) => <option key={v} value={v}>{w}</option>)}
              </select>
            </label>
            <label className="conv-row">
              <span>Retirement date</span>
              <input type="date" value={emp.retirementDate || ''} disabled={e.readOnly} aria-label={`${p.name} retirement date`}
                onChange={ev => e.setField(`employment.${p.who}.retirementDate`, ev.target.value)} />
            </label>
            <label className="conv-row">
              <span>or retirement age</span>
              <input type="text" inputMode="numeric" className="conv-text" value={emp.retirementAge || ''}
                disabled={e.readOnly || Boolean(emp.retirementDate)} aria-label={`${p.name} retirement age`}
                onChange={ev => e.setField(`employment.${p.who}.retirementAge`, ev.target.value)} />
            </label>
          </div>
        );
      })}
      <p className="conv-quiet">A date is used as given; an age is counted from the current age in the questionnaire.</p>
    </div>
  );
}

/** The household's own words, one thought per line. */
export function ToldEditor() {
  const e = useMeetingEdits();
  return (
    <textarea
      className="conv-told-input"
      aria-label="What the household told us, one thought per line"
      value={e.value('goals.clientGoals') || ''}
      disabled={e.readOnly}
      onChange={ev => e.setField('goals.clientGoals', ev.target.value)}
    />
  );
}

/**
 * The goals page's own list, kept with the meeting's choices. Lines are kept
 * as typed (blank ones too) so typing is not interrupted; the page drops the
 * blank ones.
 */
export function PageGoalsEditor() {
  const e = useMeetingEdits();
  const { setMeeting } = useMeetingContext();
  const raw = e.value('assessment.meta.meeting.goals');
  return (
    <textarea
      className="conv-told-input"
      aria-label="The goals on this page, one per line"
      value={Array.isArray(raw) ? raw.join('\n') : ''}
      disabled={e.readOnly}
      onChange={ev => setMeeting('goals', ev.target.value.split('\n'))}
    />
  );
}

const ESTATE_KEYS = [
  ['Trust', 'Living trust'],
  ['Will', 'Will'],
  ['FPOA', 'Financial power of attorney'],
  ['MPOA', 'Medical power of attorney'],
];

/** The questionnaire's estate multi-select, with None exclusive as it is there. */
export function EstateEditor() {
  const e = useMeetingEdits();
  const raw = e.value('goals.estatePlan');
  const chosen = estateDocCodes(raw);
  const toggle = key => {
    if (key === 'None') {
      e.setField('goals.estatePlan', chosen.includes('None') ? [] : ['None']);
      return;
    }
    const without = chosen.filter(k => k !== 'None');
    e.setField('goals.estatePlan', without.includes(key) ? without.filter(k => k !== key) : [...without, key]);
  };
  return (
    <div className="conv-rows">
      {ESTATE_KEYS.map(([key, label]) => (
        <label className="conv-check" key={key}>
          <input type="checkbox" checked={chosen.includes(key)} disabled={e.readOnly} onChange={() => toggle(key)} />
          <span>{label}</span>
        </label>
      ))}
      <label className="conv-check">
        <input type="checkbox" checked={chosen.includes('None')} disabled={e.readOnly} onChange={() => toggle('None')} />
        <span>None of these</span>
      </label>
      <label className="conv-row">
        <span>Established or last updated</span>
        <input type="text" inputMode="numeric" className="conv-text" value={e.value('goals.yearsEstablished') || ''}
          disabled={e.readOnly} aria-label="Year estate documents were established or updated"
          onChange={ev => e.setField('goals.yearsEstablished', ev.target.value)} />
      </label>
      <p className="conv-quiet">Nothing ticked means it has not been discussed yet.</p>
    </div>
  );
}

export const COVERAGE_TYPES = {
  LTC: { types: ['LTC'], label: 'long-term care' },
  Umbrella: { types: ['Umbrella/Liability'], label: 'umbrella liability' },
  Life: { types: ['Term Life', 'Whole Life', 'Universal Life'], label: 'life insurance' },
  Disability: { types: ['Disability'], label: 'disability' },
};

/** The policies behind one kind of coverage, and the "asked, has none" marker. */
export function CoverageEditor({ kind }) {
  const e = useMeetingEdits();
  const spec = COVERAGE_TYPES[kind];
  const path = 'insurance.policies';
  const rows = e.rows(path).map((r, i) => [r, i]).filter(([r]) => r && spec.types.includes(String(r.type || '').trim()));
  const noneRaw = e.value('insurance.noneOf');
  const none = Array.isArray(noneRaw) ? noneRaw : String(noneRaw || '').split(',').map(s => s.trim()).filter(Boolean);
  const markedNone = none.includes(kind);
  return (
    <div className="conv-rows">
      {rows.map(([r, i]) => (
        <div className="conv-rowgroup" key={r._uid || i}>
          <div className="conv-rowgroup-head">{r.type}</div>
          <label className="conv-row">
            <span>Company</span>
            <TextInput value={r.company} disabled={e.readOnly} label={`${r.type} company`}
              onChange={v => e.setRow(path, i, { company: v })} />
          </label>
          <label className="conv-row">
            <span>Benefit</span>
            <MoneyInput value={r.benefit} disabled={e.readOnly} label={`${r.type} benefit`}
              onChange={v => e.setRow(path, i, { benefit: v })} />
          </label>
        </div>
      ))}
      {!rows.length && (
        <label className="conv-check">
          <input type="checkbox" checked={markedNone} disabled={e.readOnly}
            onChange={() => e.setField('insurance.noneOf', markedNone ? none.filter(k => k !== kind) : [...none, kind])} />
          <span>{`Asked, and there is no ${spec.label}`}</span>
        </label>
      )}
      {!e.readOnly && (
        <AddButton onClick={() => {
          if (markedNone) e.setField('insurance.noneOf', none.filter(k => k !== kind));
          e.addRow(path, { type: spec.types[0], company: '', benefit: '' });
        }}>{`Add ${spec.label === 'life insurance' ? 'a life policy' : `${spec.label} coverage`}`}</AddButton>
      )}
    </div>
  );
}

/**
 * The heading the goal sheet opens with. It starts as the goal that fits the
 * household's stage of life; the advisor can pick another stage's or write
 * their own. Kept on the assessment record, and blank there means "use the
 * suggestion", so a household whose stage changes gets the new one.
 */
export function GoalLeadEditor() {
  const { data, readOnly } = useMeetingEdits();
  const { setMeeting, goalLead } = useMeetingContext();
  const own = String(data?.assessment?.meta?.meeting?.goalLead || '');
  const suggested = goalLead?.suggested || PROFILES.nearRetirement.goal;
  const current = own.trim() || suggested;
  const presets = PROFILE_ORDER.map(k => PROFILES[k]);
  const written = presets.some(p => p.goal === current) ? '' : own;
  return (
    <div className="conv-rows">
      {presets.map(p => (
        <label className="conv-check conv-lead-choice" key={p.key}>
          <input
            type="radio"
            name="conv-goal-lead"
            checked={current === p.goal}
            disabled={readOnly}
            onChange={() => setMeeting('goalLead', p.goal === suggested ? '' : p.goal)}
          />
          <span>
            <strong>{p.goal}</strong>
            <small>{`${p.label}${p.goal === suggested ? ' · suggested' : ''}`}</small>
          </span>
        </label>
      ))}
      <label className="conv-row">
        <span>Or in your own words</span>
        <input
          type="text"
          className="conv-text"
          aria-label="First goal, in your own words"
          value={written}
          placeholder="Write the goal"
          disabled={readOnly}
          onChange={e => setMeeting('goalLead', e.target.value)}
        />
      </label>
      {goalLead?.why ? <p className="conv-quiet">{`Suggested because ${goalLead.why}.`}</p> : null}
    </div>
  );
}

/**
 * The advisory fee the household pays today, from the fee section of the
 * statement. Nothing in the questionnaire holds it, so it lives on the
 * assessment record until the statement reader extracts it.
 */
export function AdvisoryFeeEditor() {
  const { data, readOnly } = useMeetingEdits();
  const { setMeeting } = useMeetingContext();
  const value = data?.assessment?.meta?.meeting?.advisoryFeePct ?? '';
  return (
    <div className="conv-rows">
      <div className="conv-row">
        <span>Advisory fee, as a percentage a year</span>
        <span className="conv-money conv-pct">
          <input
            type="text"
            inputMode="decimal"
            aria-label="Advisory fee percent"
            value={value}
            placeholder="Not recorded"
            disabled={readOnly}
            onChange={e => setMeeting('advisoryFeePct', e.target.value)}
          />
          <span aria-hidden="true">%</span>
        </span>
      </div>
      <p className="conv-quiet">Leave it blank when there is no advisor today. Pure’s own fee never appears on this page.</p>
    </div>
  );
}
